import { Component, OnInit, Input, ViewChild, forwardRef } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';

import { QuestionBase, DropdownQuestion, HiddenQuestion, EntityQuestion } from 'core/components/forms';
import { ProfileService } from 'core/services/profile.service';
import { ClientsService } from 'core/services/clients.service';
import { ModalComponent } from 'core/components/modal/modal.component';
import { ItinerariesService } from 'core/services/itineraries.service';

@Component({
  selector: 'lms-clients-publish-selector',
  templateUrl: './clients-publish-selector.component.html',
  styleUrls: ['./clients-publish-selector.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ClientsPublishSelectorComponent),
      multi: true
    }
  ]
})
export class ClientsPublishSelectorComponent implements OnInit, ControlValueAccessor {
  @Input() publish: any; // TODO Is this actually an input property?
  @Input() public focus: boolean = false; // TODO Is this actually an input property?
  @Input() question: QuestionBase<any>;

  @ViewChild('modalClientsPublishSelector', { static: false }) public modalClientsPublishSelector: ModalComponent;
  @ViewChild('modalConfirmPublishModeChange', { static: false }) public modalConfirmPublishModeChange: ModalComponent;

  resume: string;
  editData: any;
  public questions: QuestionBase<any>[] = [];
  public userRole;
  id = `lms-clients-publish-selector-${Date.now()}`;

  public propagateChange = (_: any) => {};
  public propagateTouched = (_: any) => {};

  public affectedItineraries = [];

  private previousPublishData: any = {};
  private unconfirmedPublishData: any = {};
  private courseId: string;
  private currentLang: string;

  constructor(
    public profileService: ProfileService,
    public clients: ClientsService,
    private itinerariesService: ItinerariesService,
    public translate: TranslateService
  ) {
    profileService.getLoggedUserRole$().subscribe((userRole) => (this.userRole = userRole));
  }

  ngOnInit() {
    this.currentLang = this.translate.currentLang;
    this.courseId = this.question.courseId;
    this.setResume();
  }

  writeValue(value: any) {
    this.publish = value;
    this.previousPublishData = value;
    this.setResume();
  }

  setResume() {
    let resume = '';
    if (this.publish) {
      const mode = this.publish
        ? this.translate.instant(`courses.config.clientsPublishModes.${this.publish.mode}`, {
            account: this.question.owner ? this.question.owner : 'owner'
          })
        : '';
      const amount = ['included', 'excluded'].includes(this.publish.mode) ? `(${this.publish.clientIds.length})` : '';
      resume = `${mode}${amount}`;
    }
    this.resume = resume;
  }

  registerOnChange(fn) {
    this.propagateChange = fn;
  }

  registerOnTouched(fn) {
    this.propagateTouched = fn;
  }

  setQuestions() {
    const questions = [
      new DropdownQuestion({
        key: 'mode',
        label: 'courses.config.clientsWithAccess',
        options: [
          { key: 'public', value: this.translate.instant('courses.config.clientsPublishModes.public') },
          { key: 'included', value: this.translate.instant('courses.config.clientsPublishModes.included') },
          { key: 'excluded', value: this.translate.instant('courses.config.clientsPublishModes.excluded') },
          {
            key: 'private',
            value: this.translate.instant('courses.config.clientsPublishModes.private', {
              //TODO: a label for
              account: this.question.owner ? this.question.owner : 'owner'
            })
          }
        ],
        value: this.editData.mode,
        eventHandlers: {
          onChange: (value) => this.onPublishModeChanged(value)
        }
      }),
      new HiddenQuestion({
        key: 'scope',
        value: this.editData.scope
      }),
      new HiddenQuestion({
        key: 'active',
        value: this.editData.active
      }),
      this.getClientsQuestion()
    ];

    this.questions = questions;
  }

  getClientsQuestion() {
    if (['included', 'excluded'].includes(this.editData.mode)) {
      return new EntityQuestion({
        key: 'clientIds',
        label: this.editData.mode === 'included' ? 'courses.config.clientsIncluded' : 'courses.config.clientsExcluded',
        title: 'actions.selectClient',
        value: this.editData.clientIds,
        entityOptions: {
          multiple: true,
          preload: this.editData.clientIds ? this.editData.clientIds : true
        },
        validators: { required: true },
        dataProvider: this.clients
      });
    }
    return new HiddenQuestion({
      key: 'clientIds',
      validators: { required: false },
      value: []
    });
  }

  onPublishModeChanged(mode) {
    if (mode != this.editData.mode) {
      const index = this.questions.findIndex((q) => q.key === 'clientIds');
      this.editData.mode = mode;
      this.questions.splice(2, index, this.getClientsQuestion());
    }
  }

  onEdit() {
    if (this.question.editable && !this.question.disabled) {
      this.editData = {
        mode: 'private',
        clientIds: null,
        ...this.publish,
        scope: this.userRole.scope === 'partner' ? this.profileService.getFixedPartnerId() : null
      };
      this.setQuestions();
      this.modalClientsPublishSelector.open();
    }
  }

  async onSave(publishData) {
    this.unconfirmedPublishData = this.editData = publishData;
    if (!this.unconfirmedPublishData.scope) {
      this.unconfirmedPublishData.scope = null;
    }

    this.affectedItineraries = [];
    if (this.courseId && this.isNewPublishMoreRestrictive(this.unconfirmedPublishData, this.previousPublishData)) {
      this.affectedItineraries = await this.itinerariesService.getItinerariesByCourse(this.courseId, this.currentLang);
    }
    if (this.affectedItineraries.length) {
      this.modalConfirmPublishModeChange.open();
    } else {
      this.onConfirm();
    }
  }

  /**
   * Returns true if new publish configuration is more restrictive and false otherwise.
   *
   * Public   -> public: false
   *          -> excluded: true
   *          -> included: true
   *          -> private: true
   * Excluded -> public: false
   *          -> excluded:
   *               true when a client that was not excluded now is
   *               false otherwise
   *          -> included: true (inclusion is more restrictive than exclusion)
   *          -> private: true
   * Included -> public: false
   *          -> excluded:
   *               true when a client that was included is now excluded
   *               false otherwise
   *          -> included:
   *               true when a client that was included now is not
   *               false otherwise
   *          -> private: true
   * Private  -> public: false
   *          -> excluded: false
   *          -> included: false
   *          -> private: false
   *
   * @param newPublish      New publish configuration.
   * @param previousPublish Previous publish configuration.
   */
  private isNewPublishMoreRestrictive(newPublish, previousPublish): boolean {
    if (newPublish.mode === 'public' || previousPublish.mode === 'private') {
      // 'public' is the least restrictive mode || any mode will be less restrictive than 'private'
      return false;
    }
    if (previousPublish.mode === 'public' || newPublish.mode === 'private') {
      // Any mode will be more restrictive than 'public' || 'private' is the most restrictive mode
      return true;
    }

    if (newPublish.mode === 'included') {
      if (
        previousPublish.mode === 'included' &&
        previousPublish.clientIds.some((clientId) => !newPublish.clientIds.includes(clientId))
      ) {
        // A client that was included now is not
        return true;
      }

      if (previousPublish.mode === 'excluded') {
        // Inclusion is more restrictive than exclusion
        return true;
      }
    }

    if (newPublish.mode === 'excluded') {
      if (
        previousPublish.mode === 'excluded' &&
        newPublish.clientIds.some((clientId) => !previousPublish.clientIds.includes(clientId))
      ) {
        // A client that was not excluded now is
        return true;
      }

      if (
        previousPublish.mode === 'included' &&
        newPublish.clientIds.some((clientId) => previousPublish.clientIds.includes(clientId))
      ) {
        // A client that was included is now excluded
        return true;
      }
    }

    return false;
  }

  onCancelSave() {
    this.modalClientsPublishSelector.close();
    this.modalConfirmPublishModeChange.close();
  }

  onConfirm() {
    this.publish = this.unconfirmedPublishData;
    // TODO Ideally, previousPublishData should only be set on actual submit
    this.previousPublishData = this.publish;
    this.setResume();
    this.propagateChange(this.publish);
    this.modalClientsPublishSelector.close();
    this.modalConfirmPublishModeChange.close();
  }
}
